import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  /**
   * data-allow-external-overlay 다이얼로그는 body로 옮길 때 ref 트리 밖으로 나가므로,
   * Component refs 대신 이 노드 참조를 쓴다(requiredRefs 비움).
   * @type {HTMLDialogElement | null}
   */
  #overlayDialog = null;

  /** @type {{ parent: Node; before: ChildNode | null } | null} */
  #portalRestore = null;

  #pointerDownOutside = false;

  requiredRefs = ['dialog'];

  /** @returns {HTMLDialogElement} */
  #dialogEl() {
    const el = this.#overlayDialog ?? this.refs.dialog;
    if (!(el instanceof HTMLDialogElement)) {
      throw new Error('dialog-component: missing dialog ref');
    }
    return el;
  }

  connectedCallback() {
    const overlay = this.querySelector(
      'dialog[ref="dialog"][data-allow-external-overlay]'
    );
    if (overlay instanceof HTMLDialogElement) {
      this.#overlayDialog = overlay;
      this.requiredRefs = [];
    }

    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const dialog = this.#dialogEl();

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.documentElement.style.overflow = 'hidden';

      // showModal()은 Top layer에 올라가서, body에 붙는 제3자 오버레이(예: Crema 리뷰 작성 iframe,
      // z-index 999999)보다 항상 위에 그려진다. data-allow-external-overlay가 있으면 show()로
      // 일반 스택에 두고, main 안에 두면 헤더(z-index)보다 아래에 깔리므로 dialog를 body로 옮긴다.
      if (dialog.hasAttribute('data-allow-external-overlay')) {
        if (dialog.parentNode !== document.body) {
          this.#portalRestore = {
            parent: /** @type {Node} */ (dialog.parentNode),
            before: dialog.nextSibling,
          };
          document.body.appendChild(dialog);
        }
        dialog.show();
      } else {
        dialog.showModal();
      }
      dialog.focus();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const dialog = this.#dialogEl();

    if (!dialog.open) return;

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#pointerDownOutside = false;

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.style.overflow = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.#portalRestore) {
      const { parent, before } = this.#portalRestore;
      if (before && before.parentNode === parent) {
        parent.insertBefore(dialog, before);
      } else {
        parent.appendChild(dialog);
      }
      this.#portalRestore = null;
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.#dialogEl().open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Tracks where the pointer interaction starts.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown(event) {
    const dialog = this.#dialogEl();
    this.#pointerDownOutside = isClickedOutside(event, dialog);
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const dialog = this.#dialogEl();
    const isOutsideClick = isClickedOutside(event, dialog);
    const shouldClose = this.#pointerDownOutside && isOutsideClick;
    this.#pointerDownOutside = false;

    if (shouldClose) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
